import { msg } from "@lingui/core/macro";
import { For, Show } from "solid-js";
import { useI18n } from "@/providers/i18n-provider";
import { Link } from "@tanstack/solid-router";
import { Calendar, CheckSquare, Inbox, Star, Sun } from "lucide-solid";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { COMPACT_THRESHOLD, formatCompactNumber } from "@/utils/number";

interface SmartViewStats {
  all: number;
  myDay: number;
  important: number;
  planned: number;
  inbox: number;
}

interface DashboardSmartViewsProps {
  stats: Partial<SmartViewStats> | undefined;
}

export function DashboardSmartViews(props: DashboardSmartViewsProps) {
  const { t } = useI18n();

  const SMART_VIEWS = [
    {
      to: "/my-day",
      label: msg`My Day`,
      icon: Sun,
      color: "#f59e0b",
      statKey: "myDay" as const,
    },
    {
      to: "/important",
      label: msg`Important`,
      icon: Star,
      color: "#a855f7",
      statKey: "important" as const,
    },
    {
      to: "/planned",
      label: msg`Planned`,
      icon: Calendar,
      color: "#3b82f6",
      statKey: "planned" as const,
    },
    {
      to: "/all",
      label: msg`Tasks`,
      icon: CheckSquare,
      color: "#2dd4bf",
      statKey: "all" as const,
    },
    {
      to: "/inbox",
      label: msg`Inbox`,
      icon: Inbox,
      color: "#94a3b8",
      statKey: "inbox" as const,
    },
  ];

  return (
    <div class="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-5 w-full">
      <For each={SMART_VIEWS}>
        {(view) => (
          <Link
            to={view.to}
            class="group flex flex-row lg:flex-col lg:items-start lg:justify-start items-center gap-4 lg:gap-1 rounded-2xl border border-border/40 bg-card/5 backdrop-blur-sm p-4 lg:p-5 transition-all duration-300 hover:bg-accent/10 hover:border-accent/30 active:scale-[0.98] w-full min-h-[80px] lg:min-h-[145px]"
          >
            {/* Icon Container */}
            <div
              class="lg:order-1 flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl transition-transform duration-300 group-hover:scale-110"
              style={{
                "background-color": `${view.color}20`,
                color: view.color,
              }}
            >
              <view.icon class="h-6 w-6" />
            </div>

            {/* Label Text */}
            <span class="lg:order-3 flex-1 lg:flex-none text-base lg:text-sm font-bold lg:font-semibold text-foreground/80 lg:text-foreground/60 group-hover:text-foreground transition-colors truncate lg:mt-0.5 text-left w-full">
              {t(view.label)}
            </span>

            {/* Stat Number */}
            <span class="lg:order-2 text-2xl lg:text-3xl font-bold lg:font-extrabold tracking-tight tabular-nums text-foreground/70 lg:text-foreground/80 group-hover:text-accent transition-colors shrink-0 lg:mt-2 min-h-[32px] flex items-center">
              <Show when={props.stats?.[view.statKey] !== undefined}>
                {(() => {
                  const val = props.stats?.[view.statKey] ?? 0;
                  const isCompact = val >= COMPACT_THRESHOLD;
                  const { locale } = useI18n();
                  const fullLabel = `${val.toLocaleString(locale())} ${t(view.label).toLowerCase()}`;

                  return (
                    <Show
                      when={isCompact}
                      fallback={
                        <span class="select-none" aria-label={fullLabel}>
                          {val}
                        </span>
                      }
                    >
                      <Tooltip openDelay={350} closeDelay={100}>
                        <TooltipTrigger class="focus-visible:outline-none select-none cursor-help">
                          <span
                            class="border-b border-dashed border-current pb-0.5"
                            aria-label={fullLabel}
                          >
                            {formatCompactNumber(val, locale())}
                          </span>
                        </TooltipTrigger>
                        <TooltipContent class="max-w-[200px] text-center font-semibold bg-background text-foreground border border-border/40 shadow-lg px-2.5 py-1.5 rounded-lg select-none">
                          {fullLabel}
                        </TooltipContent>
                      </Tooltip>
                    </Show>
                  );
                })()}
              </Show>
            </span>
          </Link>
        )}
      </For>
    </div>
  );
}
